import React from 'react';
import { Card, Tag, Tooltip, Space, Typography, Row, Col } from 'antd';
import {
  SafetyCertificateOutlined,
  EnvironmentOutlined,
  HeartOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  StarOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

interface Certification {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  level: 'bronze' | 'silver' | 'gold' | 'platinum';
  category: 'environmental' | 'social' | 'governance' | 'quality';
  issuer: string;
  validUntil?: string;
  verificationUrl?: string;
}

interface CertificationBadgesProps {
  brandId: number;
  certifications?: Certification[];
}

const CertificationBadges: React.FC<CertificationBadgesProps> = ({
  brandId,
  certifications = [],
}) => {
  const { t } = useTranslation();
  // 预定义的认证标准
  const predefinedCertifications: Certification[] = [
    {
      id: 'bcorp',
      name: t('badges.bcorp_name'),
      description: t('badges.bcorp_desc'),
      icon: <SafetyCertificateOutlined />,
      color: '#1890ff',
      level: 'gold',
      category: 'governance',
      issuer: 'B Lab',
      validUntil: '2025-12-31',
    },
    {
      id: 'gots',
      name: t('badges.gots_name'),
      description: t('badges.gots_desc'),
      icon: <EnvironmentOutlined />,
      color: '#52c41a',
      level: 'gold',
      category: 'environmental',
      issuer: 'Global Organic Textile Standard',
      validUntil: '2024-08-15',
    },
    {
      id: 'fairtrade',
      name: t('badges.fairtrade_name'),
      description: t('badges.fairtrade_desc'),
      icon: <HeartOutlined />,
      color: '#faad14',
      level: 'silver',
      category: 'social',
      issuer: 'Fairtrade International',
      validUntil: '2025-03-20',
    },
    {
      id: 'cradle2cradle',
      name: t('badges.cradle2cradle_name'),
      description: t('badges.cradle2cradle_desc'),
      icon: <GlobalOutlined />,
      color: '#13c2c2',
      level: 'platinum',
      category: 'environmental',
      issuer: 'Cradle to Cradle Products Innovation Institute',
    },
    {
      id: 'rainforest',
      name: t('badges.rainforest_name'),
      description: t('badges.rainforest_desc'),
      icon: <EnvironmentOutlined />,
      color: '#52c41a',
      level: 'gold',
      category: 'environmental',
      issuer: 'Rainforest Alliance',
      validUntil: '2024-11-30',
    },
    {
      id: 'leed',
      name: t('badges.leed_name'),
      description: t('badges.leed_desc'),
      icon: <EnvironmentOutlined />,
      color: '#389e0d',
      level: 'silver',
      category: 'environmental',
      issuer: 'U.S. Green Building Council',
    },
  ];

  // 模拟品牌获得的认证（实际应该从API获取）
  const brandCertifications = predefinedCertifications.slice(
    0,
    Math.floor(Math.random() * 4) + 2
  );

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'bronze':
        return '#cd7f32';
      case 'silver':
        return '#c0c0c0';
      case 'gold':
        return '#ffd700';
      case 'platinum':
        return '#e5e4e2';
      default:
        return '#1890ff';
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'bronze':
        return '🥉';
      case 'silver':
        return '🥈';
      case 'gold':
        return '🥇';
      case 'platinum':
        return '💎';
      default:
        return '⭐';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'environmental':
        return <EnvironmentOutlined />;
      case 'social':
        return <HeartOutlined />;
      case 'governance':
        return <SafetyCertificateOutlined />;
      case 'quality':
        return <StarOutlined />;
      default:
        return <CheckCircleOutlined />;
    }
  };

  const renderCertificationCard = (cert: Certification) => {
    const isExpiringSoon =
      cert.validUntil &&
      new Date(cert.validUntil) <
        new Date(Date.now() + 90 * 24 * 60 * 60 * 1000);

    return (
      <Card
        key={cert.id}
        size="small"
        style={{
          marginBottom: 12,
          border: `2px solid ${cert.color}`,
          borderRadius: 8,
        }}
        styles={{ body: { padding: 12 } }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div
            style={{
              fontSize: 24,
              color: cert.color,
              minWidth: 32,
              textAlign: 'center',
            }}
          >
            {cert.icon}
          </div>

          <div style={{ flex: 1 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 4,
              }}
            >
              <Text strong style={{ color: cert.color }}>
                {cert.name}
              </Text>
              <span style={{ fontSize: 16 }}>{getLevelIcon(cert.level)}</span>
              <Tag color={cert.color}>{cert.level.toUpperCase()}</Tag>
            </div>

            <Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', marginBottom: 8 }}
            >
              {cert.description}
            </Text>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Space size="small">
                {getCategoryIcon(cert.category)}
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {cert.issuer}
                </Text>
              </Space>

              {cert.validUntil && (
                <Text
                  type={isExpiringSoon ? 'warning' : 'secondary'}
                  style={{ fontSize: 11 }}
                >
                  {t('badges.valid_until')}:{' '}
                  {new Date(cert.validUntil).toLocaleDateString()}
                </Text>
              )}
            </div>

            {cert.verificationUrl && (
              <div style={{ marginTop: 8 }}>
                <a
                  href={cert.verificationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 11 }}
                >
                  {t('badges.verify')}
                </a>
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  };

  const renderCertificationSummary = () => {
    const categoryCount = brandCertifications.reduce(
      (acc, cert) => {
        acc[cert.category] = (acc[cert.category] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    const levelCount = brandCertifications.reduce(
      (acc, cert) => {
        acc[cert.level] = (acc[cert.level] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    return (
      <Card
        size="small"
        style={{ marginBottom: 16, backgroundColor: '#f6ffed' }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>{t('badges.summary_category')}</Text>
            <div style={{ marginTop: 8 }}>
              {Object.entries(categoryCount).map(([category, count]) => (
                <div
                  key={category}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 4,
                  }}
                >
                  <Space size="small">
                    {getCategoryIcon(category)}
                    <Text style={{ fontSize: 12 }}>
                      {category === 'environmental'
                        ? t('badges.category_environmental')
                        : category === 'social'
                          ? t('badges.category_social')
                          : category === 'governance'
                            ? t('badges.category_governance')
                            : t('badges.category_quality')}
                    </Text>
                  </Space>
                  <Text strong style={{ fontSize: 12 }}>
                    {count}
                  </Text>
                </div>
              ))}
            </div>
          </Col>

          <Col span={12}>
            <Text strong>{t('badges.summary_level')}</Text>
            <div style={{ marginTop: 8 }}>
              {Object.entries(levelCount).map(([level, count]) => (
                <div
                  key={level}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 4,
                  }}
                >
                  <Space size="small">
                    <span style={{ fontSize: 14 }}>{getLevelIcon(level)}</span>
                    <Text style={{ fontSize: 12, textTransform: 'capitalize' }}>
                      {level}
                    </Text>
                  </Space>
                  <Text strong style={{ fontSize: 12 }}>
                    {count}
                  </Text>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </Card>
    );
  };

  if (brandCertifications.length === 0) {
    return (
      <Card
        title={
          <Space>
            <SafetyCertificateOutlined />
            <span>{t('badges.empty_title')}</span>
          </Space>
        }
      >
        <div style={{ textAlign: 'center', padding: '24px', color: '#999' }}>
          <SafetyCertificateOutlined
            style={{ fontSize: 48, marginBottom: 16 }}
          />
          <div>{t('badges.empty_desc')}</div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <SafetyCertificateOutlined />
          <span>
            {t('badges.title_with_count', {
              count: brandCertifications.length,
            })}
          </span>
        </Space>
      }
    >
      {renderCertificationSummary()}

      <div>{brandCertifications.map(renderCertificationCard)}</div>

      <div
        style={{
          marginTop: 16,
          padding: 12,
          backgroundColor: '#f0f0f0',
          borderRadius: 6,
          textAlign: 'center',
        }}
      >
        <Text type="secondary" style={{ fontSize: 11 }}>
          {t('badges.disclaimer')}
        </Text>
      </div>
    </Card>
  );
};

export default CertificationBadges;
